import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { SubstitutionSchema } from "../Forms/schema/YupSchema";
import { AuthContext } from "../../context/AuthContext";
import LoaderBilan from "../LoaderBilan";
import FormHoldingSelector from "./FormHoldingSelector";

const HIDDEN_HOLDING_IDS = [9, 25, 8];

const HoldingSelector = ({ sideOpen, setSideOpen, setEntrepriseName }) => {
  const { user, apiUrl, fetchRoles, fetchUser } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [userHolding, setUserHolding] = useState({});
  const [isAppSyncing, setIsAppSyncing] = useState(false);
  const [optimisticEntrepriseName, setOptimisticEntrepriseName] = useState("");
  const [pendingHoldingChange, setPendingHoldingChange] = useState(false);

  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  useEffect(() => {
    if (user?.holdingName || user?.entrepriseName) {
      setOptimisticEntrepriseName("");
      setEntrepriseName(user.holdingName || user.entrepriseName);
    }
  }, [user, setEntrepriseName]);

  const fetchHoldings = async () => {
    try {
      const res = await axios(`${apiUrl}commerce/holding`, {
        method: "get",
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error("Error during holding query:", error);
      throw error;
    }
  };

  const holding = useQuery({
    queryKey: ["holding"],
    queryFn: fetchHoldings,
    enabled: !!user,
  });

  useEffect(() => {
    if (user?.roles.includes("ROLE_SUPERADMIN") || user?.roles.includes("ROLE_COMMERCIAL")) {
      const holdingFiltered = holding.data?.find((item) => item.id === user.holdingId);
      setUserHolding(holdingFiltered || {});
    }
  }, [holding.data, user]);

  const fetchEntreprises = async () => {
    if (!userHolding?.id) {
      return [];
    }

    try {
      const res = await axios.get(`${apiUrl}commerce/${userHolding.id}/entreprise`, {
        withCredentials: true
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching entreprises:", error);
      return [];
    }
  };

  const entreprise = useQuery({
    queryKey: ["entreprisesHeader", userHolding?.id],
    queryFn: fetchEntreprises,
    enabled: !!userHolding?.id,
  });

  useEffect(() => {
    if (!pendingHoldingChange || entreprise.isLoading || entreprise.isFetching) {
      return;
    }

    setPendingHoldingChange(false);
  }, [pendingHoldingChange, entreprise.isLoading, entreprise.isFetching]);

  const currentEntrepriseName = useMemo(() => {
    if (optimisticEntrepriseName) {
      return optimisticEntrepriseName;
    }

    if (entreprise.data && user?.entrepriseId) {
      const matchedEntreprise = entreprise.data.find((item) => item.id === user.entrepriseId);
      if (matchedEntreprise?.nom) {
        return matchedEntreprise.nom;
      }
    }

    return user?.holdingName || user?.entrepriseName || "";
  }, [optimisticEntrepriseName, entreprise.data, user]);

  const updateUserHolding = async (data) => {
    try {
      const selectedEntreprise = entreprise.data?.find(
        (item) => Number(item.id) === Number(data.entreprise)
      );
      const nextEntrepriseName = selectedEntreprise?.nom || currentEntrepriseName;

      if (nextEntrepriseName) {
        setOptimisticEntrepriseName(nextEntrepriseName);
        setEntrepriseName(nextEntrepriseName);
      }

      setIsAppSyncing(true);
      const res = await axios.put(`${apiUrl}commerce`, data, { withCredentials: true });
      queryClient.invalidateQueries(["sites"]);
      queryClient.resetQueries();
      navigate("/");

      Promise.allSettled([fetchUser(), fetchRoles()]).finally(() => {
        setIsAppSyncing(false);
      });

      return res.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      setOptimisticEntrepriseName("");
      setIsAppSyncing(false);
    }
  };

  const formContent = useMemo(
    () => [
      {
        id: "holding",
        type: "select",
        values: holding.data
          ? [...holding.data
              .filter((item) => !HIDDEN_HOLDING_IDS.includes(item.id))
              .map((item) => ({ value: item.id, label: item.nom }))].sort((a, b) =>
              a.label.localeCompare(b.label)
            )
          : [],
        loading: holding.isLoading || holding.isFetching,
        title: "Holding",
        change: async (e) => {
          setPendingHoldingChange(true);
          await setUserHolding({ id: Number(e.target.value) });
        },
      },
      {
        id: "entreprise",
        type: "select",
        values: entreprise.data
          ? [...entreprise.data.map((item) => ({ value: item.id, label: item.nom }))].sort((a, b) =>
              a.label.localeCompare(b.label)
            )
          : [],
        loading: entreprise.isLoading || entreprise.isFetching,
        title: "Entreprise",
        autoSelectFirst: true
      },
    ],
    [holding.data, holding.isLoading, holding.isFetching, entreprise.data, entreprise.isLoading, entreprise.isFetching]
  );

  const initialValues = useMemo(
    () => ({
      holding: userHolding?.id || user?.holdingId || "",
      entreprise: user?.entrepriseId || ""
    }),
    [userHolding, user]
  );

  return (
    <>
      {(user?.roles.includes("ROLE_SUPERADMIN") || user?.roles.includes("ROLE_COMMERCIAL")) && (
        <FormHoldingSelector
          formContent={formContent}
          schema={SubstitutionSchema}
          submitFunction={updateUserHolding}
          notConfirmMessage={true}
          title={isAppSyncing ? <LoaderBilan /> : currentEntrepriseName || ""}
          action="update"
          sideOpen={sideOpen}
          setSideOpen={setSideOpen}
          initialValues={initialValues}
          isSyncing={isAppSyncing || pendingHoldingChange}
        />
      )}
    </>
  );
};

export default HoldingSelector;
